import React, { useEffect, useRef, useState } from 'react';
import { authService, serviceBundler, webSocketService } from '../../App';
import {
  Button,
  Divider,
  Form,
  InputNumber,
  Layout,
  Popover,
  Select,
  Space,
  Tour,
  Upload,
  UploadProps,
  theme,
} from 'antd';

import type { GetRef, TourProps } from 'antd';

import { Content, Footer, Header } from 'antd/es/layout/layout';
import {
  UploadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import style from './SettingPage.module.css';
import { useTranslation } from 'react-i18next';
import HeaderBar from '../../Components/Header/HeaderBar';
import { SettingsWebPortal } from '../../Models/Settings/SettingsWebportal';
import { SettingsWebportalHelper } from '../../Helpers/SettingsWebportalHelper';
import CustomPopOver from '../../Components/Settings/Popover/CustomPopOver';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { SoundData } from '../../Models/Settings/SoundData';
import { HttpHelper } from '../../Helpers/HttpHelper';
import { WsWebPortalUpdate } from '../../Models/WebSocket/Outgoing/WsWebPortalUpdate';
import { ImageGallery } from '../../Components/ImageGallery/ImageGallery';
import FooterBar from '../../Components/Footer/FooterBar';
import { TourHelper } from '../../Helpers/TourHelper';
import { BackupImportExport } from '../../Components/Devices/Forms/BackupImportExport/BackupImportExport';
import { a } from '@react-spring/web';

const { useToken } = theme;

const SettingPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);

  const [settingWebPortal, setSettingWebportal] = useState<SettingsWebPortal>();
  const [soundPlaying, setSoundPlaying] = useState<boolean>(false);
  const [soundPaused, setSoundPaused] = useState<boolean>(false);
  const [allowAudio, setAllowAudio] = useState<boolean>(false);
  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isModalBackupOpen, setIsModalBackupOpen] = useState(false);

  var [audioItem, setAudioItem] = useState<HTMLAudioElement>();

  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  const refStep1 = useRef<any>(null);
  const refStep2 = useRef<any>(null);
  const refStep3 = useRef<any>(null);
  const refStep4 = useRef<any>(null);
  const refStep5 = useRef<any>(null);
  const refStep6 = useRef<any>(null);

  const steps: TourProps['steps'] = [
    TourHelper.CreateTourSteps(
      t,
      'Wissen melding',
      'Een opgeloste melding wordt zolang getoond',
      'left',
      refStep1.current,
    ),
    TourHelper.CreateTourSteps(
      t,
      'Geluid kiezen',
      'Hier kun je een geluid kiezen dat afgespeeld wordt bij een melding',
      'left',
      refStep2.current,
    ),
    TourHelper.CreateTourSteps(
      t,
      'Geluid verwijderen',
      'Hier kun je een geluid verwijderen dat niet meer gebruikt wordt',
      'left',
      refStep3.current,
    ),
    TourHelper.CreateTourSteps(
      t,
      'Geluid testen',
      'Hier kun je het gekozen geluid testen',
      'left',
      refStep4.current,
    ),
    TourHelper.CreateTourSteps(
      t,
      'Geluid inschakelen',
      'Hier kun je het afspelen van geluiden aan- of uitzetten',
      'left',
      refStep5.current,
    ),
  ];

  useEffect(() => {
    if (loading || form) {
      FetchItems();
    }

    serviceBundler.settingsWebportalService.notificationToRefresh.subscribe(
      (value) => {
        if (value) {
          if (serviceBundler.tourService.tourLoginPageActive) {
            const timeout = setTimeout(() => {
              setCurrentTourStep(1);
            }, 100);
          }
        }
      },
    );
  }, [form, loading, allowAudio]);

  useEffect(() => {
    serviceBundler.tourService.TourSettingsPageSubject.subscribe((value) => {
      if (value) {
        if (serviceBundler.tourService.tourSettingsPageActive) {
          setCurrentTourStep(1);
        }
      }
    });
  }, [0]);

  useEffect(() => {
    if (serviceBundler.tourService.tourSettingsPageActive) {
      setCurrentTourStep(1);
    }
  }, [0]);

  const FetchItems = async () => {
    serviceBundler.settingsWebportalService
      .GetFetchStatus()
      .subscribe(async (value) => {
        if (!value) {
          return;
        }

        form.resetFields();
        setSettingWebportal(
          await serviceBundler.settingsWebportalService.GetSetting(),
        );
      });

    var audioAllowed = serviceBundler.notificationService.PlayAudioIsAllowed();
    if (audioAllowed != null) {
      setAllowAudio(audioAllowed);
    } else {
      setAllowAudio(false);
    }

    setLoading(false);
  };

  const onFinishForm = () => {};
  const onFinishFormFailed = (errorInfo: any) => {};

  const GetAvailableSounds = () => {
    if (settingWebPortal == undefined) {
      return [];
    }
    if (settingWebPortal?.availableSounds == undefined) {
      return [];
    }

    return settingWebPortal?.availableSounds.map((x: SoundData) => {
      return { value: x.id, label: x.name };
    });
  };

  const GetMappedAvailableSounds = () => {
    if (settingWebPortal == undefined) {
      return [];
    }
    if (settingWebPortal?.availableSounds == undefined) {
      return [];
    }

    return settingWebPortal?.availableSounds.map((x: SoundData) => {
      return { id: x.id, name: x.name };
    });
  };

  const GetUploadUrl = (): string => {
    return SettingsWebportalHelper.GetUploadUrl();
  };

  const GetUploadHeaders = (): any => {
    var token = authService.GetToken();
          return {
        Authorization: `Bearer ${token}`,
      }
    }

  const SafeSetting = () => {
    if (settingWebPortal) {
      serviceBundler.settingsWebportalService.SafeSetting(settingWebPortal);
      webSocketService.sendMessage(new WsWebPortalUpdate(settingWebPortal));
    }
  };

  const SetSound = (id: string) => {
    var newSetting = settingWebPortal;
    if (newSetting) {
      newSetting.currentSoundTrack =
        newSetting.availableSounds.find((x) => x.id == id)?.name ?? '';
      setSettingWebportal(newSetting);
      SafeSetting();
      StopSound();
      setAudioItem(undefined);
    }
  };

  const PossibleToPlay = () => {
    if (
      settingWebPortal?.currentSoundTrack == null ||
      settingWebPortal?.currentSoundTrack == ''
    ) {
      return true;
    }
    return false;
  };

  const SetTime = (newTime: any) => {
    if (!newTime) {
      return;
    }

    var newSetting = settingWebPortal;
    if (newSetting) {
      newSetting.timeForRemoveNotification = Number(newTime);
      setSettingWebportal(newSetting);
      SafeSetting();
    }
  };

  const RemoveSound = (item: string) => {
    var newSetting = settingWebPortal;
    if (newSetting) {
      var index = newSetting.availableSounds.findIndex((x) => x.id == item);
      if (index >= 0) {
        var oldSound = newSetting.availableSounds[index];
        if (oldSound.name == newSetting?.currentSoundTrack) {
          newSetting.currentSoundTrack = '';
        }
        serviceBundler.settingsWebportalService.RemoveSound(oldSound);
        newSetting.availableSounds.splice(index, 1);
        setSettingWebportal(new SettingsWebPortal());
      }
    }
  };

  const HandleChangeUpload: UploadProps['onChange'] = async (
    info: UploadChangeParam<UploadFile>,
  ) => {
    if (info.file.status === 'done') {
      setSettingWebportal(
        await serviceBundler.settingsWebportalService.GetSettingUpdated(),
      );
    }
  };

  const PlaySound = () => {
    setSoundPlaying(true);

    if (soundPaused) {
      audioItem?.play();
      setSoundPaused(false);
      return;
    }

    var linkToSound = settingWebPortal?.availableSounds.find(
      (x) => x.name == settingWebPortal?.currentSoundTrack,
    )?.linkToSound;

    if (linkToSound == undefined) {
      return;
    }

    let newLinkToSound = HttpHelper.GetUrlStorageBaseApi() + linkToSound;
    audioItem = new Audio(newLinkToSound);

    setSoundPlaying(true);

    console.log('Playing sound:', newLinkToSound);

    audioItem.play();
    audioItem.addEventListener('ended', (event) => {
      setSoundPlaying(false);
    });

    setAudioItem(audioItem);
  };

  const CheckPlayButton = () => {
    if (!soundPlaying || soundPaused) {
      return true;
    }
    return false;
  };

  const CheckPauseButton = () => {
    if (soundPlaying && !soundPaused) {
      return true;
    }
    return false;
  };

  const PauseSound = () => {
    audioItem?.pause();
    setSoundPaused(true);
  };

    const StopSound = () => {
    audioItem?.pause();
    setSoundPaused(false);
    setSoundPlaying(false);
  };

  const SwitchAudioAllowed = (value: boolean) => {
    setAllowAudio(value);
    serviceBundler.notificationService.AllowPlayAudio(value);
  };

  const StartSpecicifcTour = (step: number) => {
    if (currentTourStep == step) {
      return true;
    }
    return false;
  };

  const TourReady = () => {
    setCurrentTourStep(0);
    serviceBundler.tourService.EndLoginPageTour();
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={[0, 48]}>
      <Layout>
        <Tour
          open={StartSpecicifcTour(1)}
          onClose={() => TourReady()}
          steps={steps}
        />

        <Header className={style.headerStyle}>
          <HeaderBar></HeaderBar>
        </Header>

        <Content
          style={{ backgroundColor: token.colorPrimaryBg }}
          className={style.headerBlock}
        >
          <div className={style.content}>
            <Divider>{t('settings')}</Divider>

            <div className={style.centerForm}>
              <Form
                name="basic"
                style={{ width: 500 }}
                initialValues={settingWebPortal}
                onFinish={onFinishForm}
                onFinishFailed={onFinishFormFailed}
                autoComplete="off"
                form={form}
              >
                <Form.Item label={t('manageImages')} key="manageImages">
                  <Button
                    style={{ marginLeft: 30 }}
                    type="default"
                    onClick={() => setIsModalUploadOpen(true)}
                  >
                    {t('manageImages')}
                  </Button>
                </Form.Item>
                <div ref={refStep1}>
                  <Form.Item
                    label={t('timeForRemoveNotification') + ' (1-10 sec)'}
                    name="timeForRemoveNotification"
                    key="timeForRemoveNotification"
                    rules={[
                      {
                        required: true,
                        message: t('fillAValidNumber').toString(),
                      },
                    ]}
                  >
                    <InputNumber min={1} max={10} onChange={SetTime} />
                  </Form.Item>
                </div>
                <div ref={refStep2}>
                  <Form.Item
                    label={t('currentSoundTrack')}
                    name="currentSoundTrack"
                    key="currentSoundTrack"
                    rules={[{ required: true, message: 'Vul een naam in' }]}
                  >
                    <Select
                      style={{ width: 300, marginLeft: 40 }}
                      onChange={SetSound}
                      options={GetAvailableSounds()}
                      popupRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          <Space style={{ padding: '0 8px 4px' }}>
                            <Upload
                              action={GetUploadUrl()}
                              accept=".mp3"
                              onChange={HandleChangeUpload}
                              headers={GetUploadHeaders()}
                            >
                              <Button
                                style={{ marginLeft: 55 }}
                                icon={<UploadOutlined />}
                              >
                                {t('addSound')}
                              </Button>
                            </Upload>
                          </Space>
                        </>
                      )}
                    />
                  </Form.Item>
                </div>
                <div ref={refStep3}>
                  <Form.Item label={t('removeSound')} key="removeSound">
                    <Popover
                      content={
                        <CustomPopOver
                          data={GetMappedAvailableSounds()}
                          removeItem={RemoveSound}
                        />
                      }
                    >
                      <Button
                        style={{ marginLeft: 50 }}
                        icon={<DeleteOutlined />}
                        type="default"
                      >
                        {t('removeSound')}
                      </Button>
                    </Popover>
                  </Form.Item>
                </div>
                <div ref={refStep4}>
                  <Form.Item label={t('testSound')} key="testsound">
                    {CheckPlayButton() && (
                      <Button
                        disabled={PossibleToPlay()}
                        style={{ marginLeft: 82 }}
                        icon={<PlayCircleOutlined />}
                        onClick={() => PlaySound()}
                      >
                        {t('playSound')}
                      </Button>
                    )}
                    {CheckPauseButton() && (
                      <Button
                        style={{ marginLeft: 82 }}
                        icon={<PauseCircleOutlined />}
                        onClick={() => PauseSound()}
                      >
                        {t('pauseSound')}
                      </Button>
                    )}
                  </Form.Item>
                </div>
                <div ref={refStep5}>
                  <Form.Item
                    initialValue={allowAudio}
                    label={t('audioAllowed')}
                    key="audioallowed"
                    valuePropName="checked"
                  >
                    <div style={{ marginLeft: 40 }}>
                      {allowAudio && (
                        <Button onClick={() => SwitchAudioAllowed(false)}>
                          {t('enabled')}
                        </Button>
                      )}
                      {!allowAudio && (
                        <Button onClick={() => SwitchAudioAllowed(true)}>
                          {t('disabled')}
                        </Button>
                      )}
                    </div>
                  </Form.Item>
                </div>
                <div ref={refStep6}>
                  <Form.Item
                    label={'Data backup'}
                    key="audioallowed"
                    valuePropName="checked"
                  >
                    <Button
                      style={{ marginLeft: 70 }}
                      type="default"
                      onClick={() => setIsModalBackupOpen(true)}
                    >
                      {'Backup importeren/exporteren'}
                    </Button>
                  </Form.Item>
                </div>
              </Form>

              <BackupImportExport
                open={isModalBackupOpen}
                onCancel={() => setIsModalBackupOpen(false)}
              ></BackupImportExport>

              <ImageGallery
                open={isModalUploadOpen}
                onCancel={() => setIsModalUploadOpen(false)}
                onChooseFuncEnabled={false}
                onChoose={undefined}
              ></ImageGallery>
            </div>
          </div>
        </Content>
        <Footer className={style.footerStyle}>
          <FooterBar></FooterBar>
        </Footer>
      </Layout>
    </Space>
  );
};

export default SettingPage;
